/* eslint-disable max-len */
import * as yup from "yup";

import { AvatarCustom, CentralLoadingIndicator, WellnessAccordionList } from "../../../components";
import { Box, Button, CircularProgress } from "@mui/material";
import React, { useEffect, useState } from "react";

import { CloseIcon } from "../../../components/Icons";
import ExpandDownIcon from "../../../components/Icons/ExpandDownIcon";
import ExpandUpIcon from "../../../components/Icons/ExpandUpIcon";
import { IPetWithAge } from "../../../services/wellness-plan/wellness-plan.interface";
import { Listbox } from "../../../styles/global.style";
import { MODALS } from "../../../config/params";
import { useCancelWellnessPlanAutoRenewal } from "../../../services/wellness-plan/wellness-plan-renewal/wellness-plan-cancel-renewal.service";
import { useFetchWellnessPlanByPetId } from "../../../services/wellness-plan/wellness-plan.service";
import { useForm } from "react-hook-form";
import { useModal } from "../../../context/ModalContext";
import { useSearchParams } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import { useDispatch } from "react-redux";
import { toastMessageActions } from "../../../state/request";
import { MIXPANEL_EVENTS } from "../../../config/mixPanelEvents";
import { userActivityLogger } from "../../../utils/mixpanel";

const CancelRenewalSchema = yup.object({
  reason: yup.string().required("Please select a reason for cancelling"),
});

const WellnessPlanUpdateModal = () => {
  const { closeModal, openModal } = useModal();
  const { cancelRenewalWellnessPlan, response, loading } = useCancelWellnessPlanAutoRenewal();
  const [openReasonsList, setOpenReasonsList] = useState(false);
  const [searchParams] = useSearchParams();
  const petId = searchParams.get("id");
  const { getWellnessPlanByPetId, data, loading: petDataLoading } = useFetchWellnessPlanByPetId();
  const [petDetails, setPetDetails] = useState<IPetWithAge>();
  const [reasonSelected, setReasonSelected] = useState<string>("");
  const dispatch = useDispatch();


  const { handleSubmit, setValue, getValues } = useForm({
    mode: "onSubmit",
    resolver: yupResolver(CancelRenewalSchema),
    defaultValues: {
      "reasons": "",
    },
  });

  const handleChange = (value: any) => {
    setReasonSelected(value);
    setValue("reasons", value);
  };

  const handleClose = () => {
    if (petId && petDetails) {
      userActivityLogger(MIXPANEL_EVENTS.USER_CLOSES_CANCEL_RENEWAL,
        {
        // pet_id: petId,
          pet_name: petDetails.name,
          wellness_plan_name: data?.wellnessPlan[0].wellnessplanName,
        });
    }
    closeModal();
  };

  const handleCancelRenewalSubmit = () => {
    if (petId && petDetails) {
      userActivityLogger(MIXPANEL_EVENTS.USER_CANCEL_RENEWAL_TRIGGERED,
        {
          // pet_id: petId,
          pet_name: petDetails.name,
          selected_reasons: reasonSelected,
          wellness_plan_name: data?.wellnessPlan[0].wellnessplanName,
        });
      cancelRenewalWellnessPlan(petId);
    }
  };

  useEffect(()=>{
    petId && getWellnessPlanByPetId(petId);
  }, [petId]);

  useEffect(()=>{
    if (data) {
      setPetDetails(data.pet);
    }
  }, [data]);

  useEffect(()=>{
    if (response && petDetails) {
      if (response?.success) {
        userActivityLogger(MIXPANEL_EVENTS.RENEWAL_CANCELLATION_SUCCESS,
          {
            // pet_id: petId,
            pet_name: petDetails.name,
            selected_reasons: reasonSelected,
            wellness_plan_name: data?.wellnessPlan[0].wellnessplanName,
          });
        dispatch(toastMessageActions.setGlobalAlert({ severity: "success", message: response.messages[0] }));
        openModal(MODALS.CANCEL_PLAN_RENEWAL_SUCCESS);
      } else {
        userActivityLogger(MIXPANEL_EVENTS.RENEWAL_CANCELLATION_FAILURE,
          {
            // pet_id: petId,
            pet_name: petDetails.name,
            selected_reasons: reasonSelected,
            wellness_plan_name: data?.wellnessPlan[0].wellnessplanName,
            error_message: response.messages[0],
          });
      }
    }
  }, [response]);

  if (petDataLoading) {
    return <CentralLoadingIndicator />;
  }

  return (
    <form onSubmit={handleSubmit(handleCancelRenewalSubmit)} className="relative h-[95%]">
      <div className="flex justify-end mb-4">
        <CloseIcon
          onClick={()=> !loading && handleClose()}
          className={`${loading ? "text-gray-3" : "text-light-black"} cursor-pointer mt-1`}
        />
      </div>
      <Box className="h-[80%] overflow-y-auto flex flex-col gap-4 flex-1 basis-0 p-6 pe-4 pt-2">

        <div className="flex flex-col gap-1 items-center">
          <h6 className="text-base font-semibold">Cancel Plan
            Renewal for <span className="capitalize">{petDetails?.name}</span>?</h6>
          <h6 className="text-sm">You will lose all the following benefits</h6>
        </div>

        {/* Pet PLAN CARD*/}
        <div className="w-full py-3 pl-4 rounded-lg flex items-center gap-3 border border-taupe-50 bg-white">
          <AvatarCustom
            image={petDetails?.image}
            name={petDetails?.name}
            width={80}
            height={80}
            isWellnessMember={petDetails?.isWellnessplan}
          />

          <div className="flex flex-col gap-2 items-start ">
            <h4 className="font-semibold text-xl">{data?.planName}</h4>
            <h6 className="capitalize">{petDetails?.name}</h6>
          </div>

        </div>

        {/* BENEFITS LIST */}

        {petDetails && <WellnessAccordionList petSpecies={petDetails?.species}/>}

        {/* REASONS */}

        <div className="flex flex-col gap-2 w-full">
          <h6 className="text-sm font-semibold">Select a Reason for Cancelling</h6>
          <div className={`flex justify-between bg-white rounded-lg border 
          ${openReasonsList ? "border-gv-yellow" : "border-taupe-50" }
          py-2 pr-2 pl-3 items-center self-stretch cursor-pointer`} onClick={()=>setOpenReasonsList(!openReasonsList)}>

            {getValues("reasons") ? <p className="text-sm">{getValues("reasons")}</p> :
              <p className="text-gray-1 text-sm">Select a Reason</p> }

            <div>
              {openReasonsList ?
                <ExpandUpIcon width={20} height={20} /> : <ExpandDownIcon width={20} height={20} /> }
            </div>

          </div>
          {openReasonsList && (
            <div className="relative -top-2">
              <Listbox>
                {[{ label: "Move hospitals (outside of GoodVets)", value: "Move hospitals (outside of GoodVets)" },
                  { label: "Not satisfied with service", value: "Not satisfied with service" }].map((reasonsList, index) => (
                  <li key={index} onClick={() => {
                    handleChange(reasonsList.value);
                    setOpenReasonsList(false);
                  }}>
                    {reasonsList.label}
                  </li>
                ))}
              </Listbox>
            </div>
          )}
        </div>

      </Box>
      <Box className="!sticky !bottom-0 !bg-gray-2 w-full pt-8">

        <Button variant="outlined" type="submit" onClick={handleCancelRenewalSubmit}
          disabled={!getValues("reasons")}
          className="!normal-case !font-semibold !border !border-taupe-2 !bg-gray-3 !w-full">
          {loading ? <CircularProgress color="inherit" size={20} />:
            "Cancel Renewal"}
        </Button>
      </Box>
    </form>
  );
};

export default WellnessPlanUpdateModal;
